import React from 'react';
import { StudentState, Q, VQ } from '../../../store/student/types';
import { SummaryRow } from '../../Summary/SummaryRow';
import { boolToYesNo, formatCurrency } from '../../../utilities/formatting';
import {
  IOtherName,
  IAdditionalPassport,
  IFamilyMember,
  ITripToUk,
  IStudentVisa,
  IBorderRefusal,
  IExclusionOrder,
  OtherVisa,
  ISponsorDetails,
  IDocument,
  StudentVisaDocuments,
  IOtherNationality,
  UKVisaRefusal,
  NonUKVisaRefusal,
  OtherVisaDocs,
  CustomRequest,
  ICourse,
} from '../../../types';
import { getDurationFromDate } from '../../../utilities/helpers';
import { hasSpouseDetails } from '../../../utilities/pages';
import Finances from '../../sections/finance';
import VisaPrepAdditionalHistoryStep2Info from './additional-history/step-2';
import VisaPrepAdditionalHistoryStep5Info from './additional-history/step-5';
import VisaPrepAdditionalHistoryStep7Info from './additional-history/step-7';
import VisaPrepFamilyStep2 from './family/step-2';
import VisaPrepFamilyStep3 from './family/step-3';
import VisaPrepFamilyStep4 from './family/step-4';
import VisaPrepFamilyStep5 from './family/step-5';
import VisaPrepOtherTravellers from './other-travellers';
import VisaPrepUniEnrolmentStep3 from './uni-enrolment/step-3';
import Feedback from './feedback';
import './visaSummary.css';

import { passportStatusLabel } from '../../pages/personal-info/qPage7/utils';
import { UserRole } from '../../../store/auth/types';
import { hasPerm, P } from '../../../utilities/roles/permissions';
import { VisaRefusal } from 'types';
import { Box, Heading } from '@chakra-ui/react';
import { getVisaLabelFromValue } from 'data';

type IProps = StudentState & { sponsor: ISponsorDetails; userRole: UserRole };

function VisaPrepPreview(props: IProps) {
  const renderDocs = (docs: Array<IDocument>) => {
    if (docs === undefined || (docs && docs.length === 0)) {
      return null;
    }

    return docs.map((doc: IDocument, i: number) => (
      <React.Fragment key={`${doc.label}-${i}`}>
        {doc.fileNames &&
          doc.fileNames.map((fileName: string, k: number) => (
            <SummaryRow
              className={'visa-summary'}
              key={`file-${fileName}-${k}`}
              value={fileName}
              label={`${doc.label} (${i + 1}.${k + 1})`}
            />
          ))}
      </React.Fragment>
    ));
  };

  const showRisks = hasPerm(props.userRole, P.CHECKLIST_FLAGS);

  return (
    <>
      <SummaryRow
        className={'visa-summary'}
        key={Q.COUNTRY}
        value={props.personalInfo.country}
        label={'Country'}
        fullLabel={''}
      />
      <SummaryRow className={'visa-summary'} key={Q.EMAIL} value={props.core.email} label={'Email'} fullLabel={''} />
      <SummaryRow
        className={'visa-summary'}
        key={Q.PHONE_NUMBER}
        value={props.personalInfo.phoneNumber}
        label={'Phone Number'}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.GIVEN_NAME}
        value={props.personalInfo.givenName}
        label={'Given Name(s)'}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.SURNAME}
        value={props.personalInfo.surname}
        label={'Family Name'}
        fullLabel={''}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.NATIONALITY}
        value={props.personalInfo.nationality}
        label={'Nationality'}
        fullLabel={''}
      />

      <SummaryRow
        className={'visa-summary'}
        key={Q.IS_COUNTRY_OF_NATIONALITY}
        value={boolToYesNo(props.personalInfo.applyingFromHomeCountry)}
        label={'Applying From Home Country'}
        fullLabel={''}
      />
      {props.personalInfo.countryOfVisaApplication && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.COUNTRY_OF_VISA_APPLICATION}
          value={props.personalInfo.countryOfVisaApplication}
          label={'Applying For Visa In Country'}
          fullLabel={''}
        />
      )}

      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.USE_WHATSAPP}
        value={props.personalInfo.useWhatsapp}
        label={'Do you use Whatsapp?'}
        fullLabel={''}
      />
      {
        (props.personalInfo.useWhatsapp !== 'No',
        props.personalInfo.whatsAppPhoneNumber !== '' && (
          <SummaryRow
            className={'visa-summary'}
            key={Q.WHATSAPP_OTHER_NUMBER}
            value={props.personalInfo.whatsAppPhoneNumber}
            label={'WhatsApp Number'}
            fullLabel={''}
          />
        ))
      }
      <br />
      {props.offer.casReceived !== undefined && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={Q.CAS_RECEIVED}
            value={boolToYesNo(props.offer.casReceived!)}
            label={'Have you received you CAS yet?'}
            fullLabel={''}
          />
          {props.offer.casReceived && props.visa.studiesConfirmationNumber !== '' ? (
            <SummaryRow
              className={'visa-summary'}
              key={VQ.STUDIES_CONFIRMATION_NUMBER}
              value={props.visa.studiesConfirmationNumber}
              label={'CAS Number'}
              fullLabel={'What is your Confirmation of Acceptance for Studies Number?'}
            />
          ) : null}
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Other Names
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={`${Q.HAS_OTHER_NAMES}`}
        value={boolToYesNo(props.personalInfo.hasOtherNames)}
        label={`Has other names`}
        fullLabel={
          'In addition to the names already provided, are you now or have you ever been known by another name?'
        }
      />
      <SummaryRow
        className={'visa-summary'}
        value={props.personalInfo.otherNames?.length}
        label={`How many`}
        fullLabel={'How many names?'}
      />
      {props.personalInfo.otherNames?.map((otherName: IOtherName, i: number) => (
        <React.Fragment key={`other-names-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.GIVEN_NAME}-${i}`}
            value={otherName.givenName}
            label={`Previous Given Name ${i + 1}: `}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.SURNAME}-${i}`}
            value={otherName.surname}
            label={`Previous Family Name ${i + 1}: `}
          />

          <SummaryRow
            className={'visa-summary'}
            key={`${Q.OTHER_NAME_REASON}-${i}`}
            value={otherName.reason}
            label={`Reason for Change ${i + 1}: `}
          />
        </React.Fragment>
      ))}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Gender and Marital Status
      </Heading>
      <SummaryRow className={'visa-summary'} key={Q.GENDER} value={props.personalInfo.gender} label={'Gender'} />
      {props.visa.maritalStatus && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.MARITAL_STATUS}
          value={props.visa.maritalStatus!.toLowerCase()}
          label={'What is your marital status?'}
        />
      )}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.FAMILY_MEMBERS_TRAVELLING}
        value={boolToYesNo(props.offer.hasDependents)}
        label={'Do you have anyone coming with you to the UK?'}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.SPOUSE_TRAVELLING}
        value={boolToYesNo(props.offer.hasPartner)}
        label={'Are you applying for your Student visa with a dependant spouse/civil partner/unmarried partner?'}
      />
      {props.visa.maritalStatus !== undefined && hasSpouseDetails(props.visa.maritalStatus) && (
        <Box key={'partner'}>
          <Heading as="h4" fontSize={'md'}>
            Partner details
          </Heading>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_GIVEN_NAME}
            value={props.visa.spouseGivenName}
            label={'Partner Given Name'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_FAMILY_NAME}
            value={props.visa.spouseFamilyName}
            label={'Partner Family Name'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_DOB}
            value={props.visa.spouseDob}
            label={'Partner Date of Birth'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_NATIONALITY}
            value={props.visa.spouseNationality}
            label={'Partner Nationality'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_SAME_NATIONALITY}
            value={boolToYesNo(props.visa.spouseSameNationality)}
            label={'Have they always had the same nationality?'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_LIVING_WITH}
            value={boolToYesNo(props.visa.spouseLivingWith)}
            label={'Does your spouse / partner currently live with you?'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_TRAVELLING_WITH}
            value={props.visa.spouseTravellingWith}
            label={'Is this person travelling with you?'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_HAS_PASSPORT}
            value={boolToYesNo(props.visa.spouseHasPassport)}
            label={'Do they have a Passport or Travel Document?'}
          />
          {props.visa.spouseHasPassport && (
            <SummaryRow
              className={'visa-summary'}
              key={VQ.SPOUSE_DOB}
              value={props.visa.spousePassportNumber}
              label={'Current Passport or Travel Document Number:'}
            />
          )}
          <SummaryRow
            className={'visa-summary'}
            key={VQ.SPOUSE_UK_VISA}
            value={boolToYesNo(props.visa.spouseUkVisa)}
            label={'Do they have a visa for the UK?'}
          />
        </Box>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Address
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HOME_ADDRESS}
        value={props.personalInfo.homeAddress}
        label={'Permanent Residential Address'}
      />
      <SummaryRow className={'visa-summary'} key={Q.CITY} value={props.personalInfo.city} label={'City'} />
      <SummaryRow
        className={'visa-summary'}
        key={Q.POSTCODE}
        value={props.personalInfo.postCode}
        label={'Postcode (if you have one)'}
      />
      <SummaryRow className={'visa-summary'} key={Q.COUNTRY} value={props.personalInfo.country} label={'Country'} />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        value={props.visa.residentialAddressStartDate}
        label={'Residential start date'}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HOME_OWNERSHIP_STATUS}
        value={props.visa.homeOwnershipStatus}
        label={'What is the ownership status of your home?'}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_DIFFERENT_CONTACT_DETAILS}
        value={boolToYesNo(props.visa.hasDifferentContactDetails)}
        label={'Different preferred contact details'}
        fullLabel={'Are your preferred contact details different from the contact details already provided above?'}
      />
      {props.visa.hasDifferentContactDetails && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_ADDRESS}
            value={props.visa.diffContactAddress}
            label={'Contact Address'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_CITY}
            value={props.visa.diffContactCity}
            label={'City'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_POSTCODE}
            value={props.visa.diffContactPostcode}
            label={'Postcode (if you have one)'}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.DIFF_CONTACT_COUNTRY}
            value={props.visa.diffContactCountry}
            label={'Country'}
          />
        </>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.RESIDENTIAL_ADDRESS_START_DATE}
        value={getDurationFromDate(props.visa.residentialAddressStartDate)}
        label={'How long have you lived at this address?'}
      />
      {props.visa.homeOwnershipStatus === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.HOME_OWNERSHIP_STATUS_OTHER}
          value={props.visa.homeOwnershipStatusOther}
          label={'Other'}
          fullLabel={`Give more details about your living situation, such as who you live with and who owns the property`}
        />
      )}
      {/* TODO: delete it if we not need that anymore */}
      {/* <SummaryRow
        className={'visa-summary'}
        key={Q.APPLYING_FROM_HOME_COUNTRY}
        value={props.history.home_country_application}
        label={
          'Are you applying from the country where you are currently living?'
        }
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.APPLYING_FROM_NON_HOME_COUNTRY_REASON}
        value={props.history.home_country_application_reason}
        label={
          'Please tell us why you are applying from a country other than where you are currently living?'
        }
      /> */}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.NON_HOME_COUNTRY_APP_DESC}
        value={props.visa.nonHomeCountryApplicationDesc}
        label={'Immigration status and expiry date'}
        fullLabel={
          'Please explain your visa/immigration status for the country you are applying from, and the expiry date of any visas you hold for this country'
        }
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Passport
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PASSPORT_STATUS}
        value={passportStatusLabel(props.personalInfo.passportStatus)}
        label={'Do you currently have a passport?'}
      />
      {props.documents.passport &&
      (props.documents.passport as any).length &&
      (props.documents.passport as any)[0].docNumber ? (
        <Box key={'passports'}>
          <Heading as="h4" fontSize={'md'}>
            Passports
          </Heading>
          {Object.values(props.documents.passport).map((pPort, i: number) => (
            <React.Fragment key={`passport-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                key={`passport-number-${i}`}
                value={pPort.docNumber}
                label={`Passport ${i + 1}: Passport or Travel Document Number`}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-issuing-auth-${i}`}
                value={pPort.placeOfIssue}
                label={`Passport ${i + 1}: Issuing authority`}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-date-of-issue-${i}`}
                value={pPort.dateOfIssue}
                label={`Passport ${i + 1}: Date of Issue`}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`passport-date-of-expiry-${i}`}
                value={pPort.dateOfExpiry}
                label={`Passport ${i + 1}: Date of Expiry`}
              />
            </React.Fragment>
          ))}
        </Box>
      ) : null}
      {props.personalInfo.passportStatus === 'Yes' && (
        <SummaryRow
          className={'visa-summary'}
          key={`passport-expiry`}
          value={props.personalInfo.passportExpiryDate}
          label={`Passport Expiry Date`}
        />
      )}
      {props.personalInfo.passportStatus !== 'Yes' && (
        <SummaryRow
          className={'visa-summary'}
          key={`passport-expected`}
          value={props.personalInfo.passportExpectedDate}
          label={`When do you expect to receive your passport?`}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_ADDITIONAL_PASSPORTS}
        value={boolToYesNo(props.visa.hasAdditionalPassports)}
        label="Has additional passports"
        fullLabel={`Have you had any other passports in the last 10 years? (other than the ones in the document upload section)`}
      />
      {props.visa.hasAdditionalPassports && (
        <>
          <SummaryRow
            className={'visa-summary'}
            value={props.visa.additionalPassports?.length}
            label={`How many additional passports?`}
          />
          <Box key={'Additional passports'}>
            <Heading as="h4" fontSize={'md'}>
              Additional Passports
            </Heading>
            {props.visa.additionalPassports?.map((pPort: IAdditionalPassport, i: number) => (
              <React.Fragment key={`additional-passports-${i}`}>
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_PASSPORT_NUMBER}-${i}`}
                  value={pPort.docNumber}
                  label={`Additional Passport ${i + 1}: Current Passport or Travel Document Number:`}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_ISSUING_AUTHORITY}-${i}`}
                  value={pPort.issuingAuthority}
                  label={`Additional Passport ${i + 1}: Issuing Authority:`}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_DATE_OF_ISSUE}-${i}`}
                  value={pPort.issueDate}
                  label={`Additional Passport ${i + 1}: Date of Issue:`}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.ADDITIONAL_PASSPORT_DATE_OF_EXPIRY}-${i}`}
                  value={pPort.expiryDate}
                  label={`Additional Passport ${i + 1}: Date of Expiry:`}
                />
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Nationality, Country and Place of Birth
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.NATIONALITY}
        value={props.personalInfo.nationality}
        label={'Nationality'}
      />
      <SummaryRow
        className={'visa-summary'}
        key={Q.BIRTH_COUNTRY}
        value={props.personalInfo.birthCountry}
        label={'Country of Birth'}
      />
      <SummaryRow className={'visa-summary'} key={Q.DOB} value={props.personalInfo.dob} label={'Date of Birth'} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Other Nationalities
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_OTHER_NATIONALITY}
        value={boolToYesNo(props.personalInfo.hasOtherNationality)}
        label={'Other Nationality / Citizenship'}
        fullLabel={'Do you currently hold, or have you ever held, any other nationality or citizenship?'}
      />
      {props.personalInfo.hasOtherNationality ? (
        <>
          <SummaryRow
            className={'visa-summary'}
            value={props.personalInfo.otherNationalities?.length}
            label={'How many other nationalities?'}
          />
          {props.personalInfo.otherNationalities?.map((otherNationality: IOtherNationality, i: number) => (
            <React.Fragment key={`other-nationality-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                key={`${Q.OTHER_NATIONALITIES}-nationality-${i}`}
                label={`Other Nationality ${i + 1}:`}
                value={otherNationality.nationality}
              />
              <SummaryRow
                className={'visa-summary'}
                key={`${Q.OTHER_NATIONALITIES}-isCurrent-${i}`}
                label={`Other Nationality ${i + 1}: Is this a current or former nationality?`}
                value={boolToYesNo(otherNationality.isCurrent)}
              />
            </React.Fragment>
          ))}
        </>
      ) : null}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        English Language
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LANGUAGE}
        value={props.visa.language}
        label={'Interview language'}
        fullLabel={'If UKVI need to interview you, what language would you like to use in the interview?'}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_ENGLISH_TEST}
        value={props.personalInfo.hasEnglishTest}
        label="English Test"
        fullLabel="Did you submit an English test as part of your application?"
      />
      {props.personalInfo.hasEnglishTest && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.ENGLISH_TEST_TYPE}
          value={props.personalInfo.englishTestType}
          label="Type"
          fullLabel="What type of English test?"
        />
      )}
      {props.personalInfo.hasEnglishTest && props.personalInfo.englishTestType === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.ENGLISH_TEST_TYPE_OTHER}
          value={props.personalInfo.englishTestTypeOther}
          label="Other"
        />
      )}
      {props.personalInfo.hasEnglishTest !== undefined && props.personalInfo.hasEnglishTest === false && (
        <Heading as="h4" fontSize={'md'}>
          You have indicated that you have not submitted an English test as part of your application
        </Heading>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.QUALIFICATION_AS_PROOF_OF_ENGLISH}
        value={boolToYesNo(props.visa.qualificationAsProofOfEnglish)}
        label="Are you are using a previous qualification as proof of English language?"
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.QUALIFICATION_AND_COUNTRY}
        value={props.visa.qualificationAndCountry}
        label="Please state your academic qualification and the country it was taught in:"
      />
      <SummaryRow
        className={'visa-summary'}
        key={`course-in-uk-tier-4`}
        value={boolToYesNo(
          props.history.hasPreviousUkStudy &&
            props.history.hasPreviousUkStudyVisa &&
            Number(props.history.previousUkStudyVisas?.length) > 0 &&
            props.history.previousUkStudyVisas?.find(x => x.visaType === 'General Student') !== undefined,
        )}
        label="Have you successfully completed a course of study in the UK as a Tier 4 (Child) student?"
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Dependants
      </Heading>
      <VisaPrepFamilyStep5 {...props.visa} />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.CHILDREN_TRAVELLING}
        label="Applying for a Student visa with dependant Children"
        fullLabel="Are you applying for your Student visa with dependant children?"
        value={boolToYesNo(props.offer.hasChildren)}
      />
      {props.offer.hasChildren && (
        <SummaryRow
          className={'visa-summary'}
          key="children_num"
          label="Num of Children"
          fullLabel="How many children?"
          value={props.offer.childrenNum}
        />
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Parents
      </Heading>
      <VisaPrepFamilyStep2 {...props.visa} />
      <br />
      <VisaPrepFamilyStep3 {...props.visa} />
      <br />
      <VisaPrepFamilyStep4 {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        UK contact details
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.UK_PRESENCE}
        label={'Do you have an established presence in the UK?'}
        value={boolToYesNo(props.visa.ukPresence)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.UK_FAMILY}
        label={'Do you have family in the UK?'}
        value={boolToYesNo(props.visa.ukFamily)}
      />
      {props.visa.ukFamily && (
        <>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.STAYING_WITH_FAMILY}
            label={'Will you be staying with any family members?'}
            value={boolToYesNo(props.visa.stayingWithFamily)}
          />
          {props.visa.stayingWithFamily && (
            <>
              <SummaryRow
                className={'visa-summary'}
                label={'How many family members will you be staying with?'}
                value={props.visa.familyMembers?.length}
              />
              <Box key={'Family members'}>
                <Heading as="h4" fontSize={'md'}>
                  Family members
                </Heading>
                {props.visa.familyMembers?.map((familyMember: IFamilyMember, i: number) => (
                  <React.Fragment key={`family-members-${i}`}>
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.NATIONALITY}-${i}`}
                      value={familyMember.nationality}
                      label={`Family member ${i + 1}: Nationality`}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.FAMILY_NAME}`}
                      value={familyMember.familyName}
                      label={`Family member ${i + 1}: Family Name`}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={`${VQ.GIVEN_NAME}`}
                      value={familyMember.givenName}
                      label={`Family member ${i + 1}: Given Name`}
                    />
                    <SummaryRow
                      className={'visa-summary'}
                      key={VQ.DOB}
                      label={`Family member ${i + 1}: Date of Birth`}
                      value={familyMember.dob}
                    />
                  </React.Fragment>
                ))}
              </Box>
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_ADDRESS}
                label="Contact Address for Family you are staying with"
                value={props.visa.familyContactAddress}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_CITY}
                label="City"
                value={props.visa.familyContactCity}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_POSTCODE}
                label="Postcode"
                value={props.visa.familyContactPostcode}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_COUNTRY}
                label="Country"
                value={props.visa.familyContactCountry}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_NUMBER}
                label={'Contact number'}
                value={props.visa.familyPhoneNumber}
              />
              <SummaryRow
                className={'visa-summary'}
                key={VQ.FAMILY_CONTACT_EMAIL}
                label={'Contact email address'}
                value={props.visa.familyEmail}
              />
            </>
          )}
        </>
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_OTHER_CONTACT_ADDRESS}
        label={'Do you have another address in the UK?'}
        value={boolToYesNo(props.visa.hasOtherContactAddress)}
      />
      {props.visa.hasOtherContactAddress && (
        <>
          <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
            Planned non-family address
          </Heading>
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_ADDRESS}
            label="UK Contact Address"
            value={props.visa.otherContactAddress}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_CITY}
            label="City"
            value={props.visa.otherContactCity}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_POSTCODE}
            label="Postcode"
            value={props.visa.otherContactPostcode}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_COUNTRY}
            label="Country"
            value={props.visa.otherContactCountry}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.OTHER_CONTACT_NUMBER}
            label={'Contact number (if applicable)'}
            value={props.visa.otherPhoneNumber}
          />
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Other travellers
      </Heading>
      <VisaPrepOtherTravellers {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        UK Travel
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key="overseasTravel"
        label="Overseas Travel"
        fullLabel="Have you ever travelled outside of your home country in the last 10 years?"
        value={boolToYesNo(props.history.hasOverseasTravel)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_BEEN_TO_UK}
        label={'Have you ever been to the UK?'}
        value={boolToYesNo(props.visa.hasBeenToUk)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="ukTravel"
        label="Travelled to the UK in the last 10 years"
        fullLabel="Have you ever travelled to the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasOverseasTravel && props.history.hasUkTravel)}
      />
      {props.history.hasOverseasTravel && props.history.hasUkTravel && (
        <SummaryRow
          className={'visa-summary'}
          key="ukTravelTimes"
          label="How many times"
          fullLabel="Approximately how many times?"
          value={props.history.ukTravelTimes}
        />
      )}
      {props.visa.tripsToUk?.map((trip: ITripToUk, i: number) => (
        <React.Fragment key={`uk-trip-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${VQ.TRIPS_TO_UK_ARRIVAL_DATE}-${i}`}
            label={`UK trip ${i + 1}: Date of Arrival`}
            value={trip.arrivalDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${VQ.TRIPS_TO_UK_DEPARTURE_DATE}-${i}`}
            label={`UK trip ${i + 1}: Date of Departure`}
            value={trip.departureDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={VQ.TRIPS_TO_UK_PURPOSE}
            value={trip.purpose}
            label={`UK trip ${i + 1}: Purpose of Trip`}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key="prevUKStudy"
        label="Previous UK Study"
        fullLabel="Have you studied in the UK before?"
        value={boolToYesNo(props.history.hasPreviousUkStudy)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="prevUKStudyVisa"
        label="UK Study Visa"
        fullLabel="Did you receive a student visa for your previous UK study?"
        value={boolToYesNo(props.history.hasPreviousUkStudyVisa)}
      />
      {props.history.hasPreviousUkStudyVisa && props.history.hasPreviousUkStudy && (
        <SummaryRow
          className={'visa-summary'}
          key="prevUKStudyVisaNumber"
          label="Number of Visas"
          fullLabel="How many UK student visas have you received?"
          value={props.history.previousUkStudyVisas?.length}
        />
      )}
      {props.history.previousUkStudyVisas?.map((visa: IStudentVisa, i: number) => (
        <React.Fragment key={`previous-visa-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`type-${i}`}
            label={`Student Visa ${i + 1} - What type of student visa?`}
            value={getVisaLabelFromValue(visa.visaType)}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`level-${i}`}
            label={`Student Visa ${i + 1} - What was the level of Study?`}
            value={visa.level}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`year-${i}`}
            label={`Student Visa ${i + 1} - What was the year you received it?`}
            value={visa.year}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`complete-${i}`}
            label={`Student Visa ${i + 1} - Did you complete your course?`}
            value={boolToYesNo(visa.isComplete)}
          />
          {visa.isComplete === false && (
            <SummaryRow
              className={'visa-summary'}
              key={`incomplete-${i}`}
              label={`Student Visa ${i + 1} - Why did you not complete your course?`}
              value={visa.isIncompleteRecorded}
            />
          )}
          <SummaryRow
            className={'visa-summary'}
            key={`on-record-${i}`}
            label={`Student Visa ${i + 1} - Was your visa curtailed (shortened)?`}
            value={visa.isVisaShortened}
          />
          {visa.isVisaShortened && (
            <SummaryRow
              className={'visa-summary'}
              key={`curtailed-${i}`}
              label={`Student Visa ${i + 1} - What date was your visa cut?`}
              value={visa.shortenedDate}
            />
          )}
        </React.Fragment>
      ))}
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_UK_OTHER_VISA}
        label="Other UK Visas"
        fullLabel="Have you been granted any other visas (non-student) for the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasOtherUkVisas)}
      />
      {props.history.hasOtherUkVisas && (
        <>
          <SummaryRow className={'visa-summary'} label="How many?" value={props.history.otherUkVisas?.length} />
          {props.history.otherUkVisas?.map((visa: OtherVisa, i: number) => (
            <React.Fragment key={`previous-uk-visa-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                label={`Visa ${i} - What type of visa were you granted?`}
                value={getVisaLabelFromValue(visa.visaType)}
              />
              <SummaryRow
                className={'visa-summary'}
                label={`Visa ${i} - What year were you granted this visa?`}
                value={visa.year}
              />
              <SummaryRow
                className={'visa-summary'}
                label={`Visa ${i} - Is visa completed?`}
                value={boolToYesNo(visa.isComplete)}
              />
            </React.Fragment>
          ))}
        </>
      )}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_RECEIVED_MEDICAL_TREATMENT}
        label={'Have you ever received medical treatment in the UK?'}
        value={boolToYesNo(props.visa.hasReceivedMedicalTreatment)}
      />
      {props.visa.leaveToRemains && props.visa.leaveToRemains.length > 0 && (
        <>
          <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
            Leave to remain
          </Heading>
          <VisaPrepAdditionalHistoryStep5Info {...props.visa} />
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Previous UK documents
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_NI_NUMBER}
        label={'Do you have a UK National Insurance Number?'}
        value={boolToYesNo(props.visa.hasNINumber)}
      />
      {props.visa.hasNINumber && (
        <SummaryRow
          className={'visa-summary'}
          key={VQ.NI_NUMBER}
          value={props.visa.niNumber}
          label={'What is the number?'}
        />
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Commonwealth and EEA travel
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TRAVELLED}
        label={'Has travelled outside residence'}
        fullLabel={'Have you ever travelled outside your country of residence, excluding the UK, in the last 10 years?'}
        value={boolToYesNo(props.visa.hasTravelled)}
      />
      <VisaPrepAdditionalHistoryStep2Info {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Arrival
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.ARRIVAL_DATE}
        label={'Date of planned arrival in the UK'}
        value={props.visa.arrivalDate}
      />
      <SummaryRow
        className={'visa-summary'}
        title="How long do you intend to stay in the UK?"
        key={VQ.LENGTH_IN_UK_INTENTION_DD}
        label={'Days'}
        value={`${props.visa.lengthOfStayDays || 0}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LENGTH_IN_UK_INTENTION_MM}
        label={'Months'}
        value={`${props.visa.lengthOfStayMonths || 0}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.LENGTH_IN_UK_INTENTION_YY}
        label={'Years'}
        value={`${props.visa.lengthOfStayYears || 0}`}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        UK Leave, Refusals and Exclusions
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREVIOUS_VISA_REJECTION}
        label="Border Refusals"
        fullLabel="Have you been refused entry to the UK at the border in your lifetime?"
        value={boolToYesNo(props.history.hasBorderRefusal)}
      />
      <SummaryRow
        className={'visa-summary'}
        label="Number of border refusals"
        value={props.visa.borderRefusals?.length}
      />
      {props.visa.borderRefusals?.map((borderRefusal: IBorderRefusal, i: number) => (
        <>
          <React.Fragment key={`refusal-${i}`}>
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_DATE}-${i}`}
              label={`Border refusal ${i + 1}: Date of refusal`}
              value={borderRefusal.refusalDate}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_AIRPORT}-${i}`}
              value={borderRefusal.airport}
              label={`Border refusal ${i + 1}: Port or airport`}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_REASON}-${i}`}
              value={borderRefusal.reason}
              label={`Border refusal ${i + 1}: Reason`}
            />
            <SummaryRow
              className={'visa-summary'}
              key={`${VQ.BORDER_REFUSAL_REFERENCE}-${i}`}
              value={borderRefusal.reference}
              label={`Border refusal ${i + 1}: Reference number`}
            />
          </React.Fragment>
        </>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_VISA_REFUSAL}
        label="Visa Refusals"
        fullLabel="Have you ever previously received a visa refusal when attempting to travel to the UK in the last 10 years?"
        value={boolToYesNo(props.history.hasUkVisaRefusals)}
      />
      <SummaryRow
        className={'visa-summary'}
        key="visaRefusalNumber"
        label="Number"
        fullLabel="How many times?"
        value={props.history.ukVisaRefusals?.length}
      />
      {props.history.ukVisaRefusals?.map((visa: OtherVisa, i: number) => (
        <React.Fragment key={`previous-uk-visa-refusal-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-type-${i}`}
            label={`Visa ${i} - What type of visa were you granted?`}
            value={getVisaLabelFromValue(visa.visaType)}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-year-${i}`}
            label={`Visa ${i} - What year were you granted this visa?`}
            value={visa.year}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.PREVIOUS_VISA_REJECTION}-complete-${i}`}
            label={`Visa ${i} - Is visa completed?`}
            value={boolToYesNo(visa.isComplete)}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_VOLUNTARILY_DEPARTED}
        label={'Have you ever voluntarily elected to depart the UK?'}
        value={boolToYesNo(props.visa.hasVoluntarilyDeparted)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_EXCLUSION_ORDER}
        label={'Have you received, or have you been subject to, an exclusion order from the UK?'}
        value={boolToYesNo(props.visa.hasExclusionOrder)}
      />
      {props.visa.hasExclusionOrder && (
        <>
          <SummaryRow
            className={'visa-summary'}
            label="How many exclusion orders?"
            value={props.visa.exclusionOrders?.length}
          />
          <Box key={'exclusion-orders'}>
            <Heading as="h4" fontSize={'md'}>
              Exclusion orders
            </Heading>
            {props.visa.exclusionOrders?.map((exclusion: IExclusionOrder, i: number) => (
              <React.Fragment key={`exclusion-${i}`}>
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_DATE}-${i}`}
                  label={`Exclusion order ${i + 1}: Date of Exclusion`}
                  value={exclusion.exclusionDate}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_REASON}-${i}`}
                  value={exclusion.reason}
                  label={`Exclusion order ${i + 1}: Reason for exclusion`}
                />
                <SummaryRow
                  className={'visa-summary'}
                  key={`${VQ.EXCLUSION_REFERENCE}-${i}`}
                  value={exclusion.reference}
                  label={`Exclusion order ${i + 1}: Reference number`}
                />
              </React.Fragment>
            ))}
          </Box>
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Other travel
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.PREV_NON_UK_VISA_REFUSAL}
        label="Non-UK Visa Refusals"
        fullLabel="Have you received a visa refusal from any other country (non-UK) in the last 10 years?"
        value={boolToYesNo(props.history.hasOtherVisaRefusals)}
      />
      {props.history.hasOtherVisaRefusals && (
        <>
          <SummaryRow
            className={'visa-summary'}
            label="How many times?"
            value={props.history.otherVisaRefusals?.length}
          />
          {props.history.otherVisaRefusals?.map((visa: VisaRefusal, i: number) => (
            <React.Fragment key={`previous-non-uk-refusal-${i}`}>
              <SummaryRow
                className={'visa-summary'}
                label={`Non-UK Visa Refusal ${i + 1} - What country issued the refusal?`}
                value={visa.country}
              />
              <SummaryRow
                className={'visa-summary'}
                label={`Non-UK Visa Refusal ${i + 1}:  Was the refusal for a student visa?`}
                value={boolToYesNo(visa.isStudentVisa)}
              />
              <SummaryRow
                className={'visa-summary'}
                label={`Non-UK Visa Refusal ${i + 1}:  What year did you receive the refusal?`}
                value={visa.year}
              />
            </React.Fragment>
          ))}
        </>
      )}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Deportations
      </Heading>
      <VisaPrepAdditionalHistoryStep7Info {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Criminal Convictions
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.HAS_CRIMINAL_CONVICTIONS}
        label="Have you ever had any criminal convictions, charges, or penalties in the UK or any other country?"
        value={boolToYesNo(props.personalInfo.hasCriminalConvictions)}
      />
      {props.personalInfo.hasCriminalConvictions && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.CRIMINAL_CONVICTIONS_REASON}
          label="Criminal Convictions Info"
          fullLabel="Please provide more information on the date and nature of the offence(s)"
          value={props.personalInfo.criminalConvictionReason || ''}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_CRIMINAL_CHARGES}
        label={'Has criminal charges'}
        fullLabel={
          'Have you ever been charged in any country with a criminal offence for which you have not yet been tried in the court (including traffic offences)?'
        }
        value={boolToYesNo(props.visa.hasCriminalCharges)}
      />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_BAD_ACTIVITIES}
        label={'Bad activities'}
        fullLabel={
          'Have you engaged in any other activities that might indicate that you may not be admitted to the UK?'
        }
        value={boolToYesNo(props.visa.hasBadActivities)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Declarations
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_WAR_CRIMES}
        label={'War crimes'}
        fullLabel={
          'In times of either peace or war have you ever been involved in, or suspected of involvement in, war crimes, crimes against humanity or genocide?'
        }
        value={boolToYesNo(props.visa.hasWarCrimes)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_INVOLVEMENT}
        label={'Terrorism involvement'}
        fullLabel={'Have you ever been involved in, supported or encouraged terrorist activities in any country?'}
        value={boolToYesNo(props.visa.hasTerrorismInvolvement)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_CONNECTION}
        label={'Terrorism connection'}
        fullLabel={
          'Have you ever been a member of, or given support to, an organisation that has been connected with terrorism?'
        }
        value={boolToYesNo(props.visa.hasTerrorismConnection)}
      />
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.HAS_TERRORISM_JUSTIFIED}
        label={'Justified terrorism'}
        fullLabel={
          'Have you ever, by any means or medium, expressed views that justify or glorify terrorist violence, or that may encourage others to commit acts of terrorism or other serious criminal acts?'
        }
        value={boolToYesNo(props.visa.hasTerrorismJustified)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Sponsor Licence Details
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key="sponsor-licence"
        label={'What is your sponsor licence number?'}
        value={props.sponsor.licenceNumber}
      />
      <SummaryRow
        className={'visa-summary'}
        key="sponsor-address"
        label={"Sponsor's address"}
        value={props.sponsor.address}
      />
      <SummaryRow className={'visa-summary'} key="sponsor-town" label={'Town / City'} value={props.sponsor.city} />
      <SummaryRow className={'visa-summary'} key="sponsor-postcode" label={'Postcode'} value={props.sponsor.postcode} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Place of Study
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={VQ.STUDY_ADDRESS}
        label={'What is the address of the main site of study?'}
        value={props.visa.studyAddress}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        University Application
      </Heading>
      <VisaPrepUniEnrolmentStep3 {...props.visa} />
      <SummaryRow
        className={'visa-summary'}
        key="ATAS-required"
        label={'Is ATAS required'}
        value={boolToYesNo(!!(props.documents.atas && props.documents.atas.length))}
      />
      {props.documents.atas && props.documents.atas.length ? (
        <SummaryRow
          className={'visa-summary'}
          key="ATAS-applied"
          label={'Have you applied for your ATAS Certificate?'}
          value={boolToYesNo(Object.values(props.documents.atas).find(x => x.isAtasPending === true) !== undefined)}
        />
      ) : null}
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Funding
      </Heading>
      {/* TODO
      <SummaryRow
        className={'visa-summary'}
        key="course-fee"
        label={'Course Fee'}
        value={`£${formatCurrency(props.financials.courseFee)}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key="pre-sessional-fee"
        label={'Pre-Sessional Fee'}
        value={`£${formatCurrency(props.financials.preSessionalCourseFee)}`}
      />
      <SummaryRow
        className={'visa-summary'}
        key="living-cost"
        label={'Living cost'}
        value={`£${formatCurrency(
          props.financials.fundingSummary.studentLivingCost.amount
        )}`}
      />
      */}
      <SummaryRow
        className={'visa-summary'}
        key="dependants-living-cost"
        label={'Dependants living cost'}
        value={`£${formatCurrency(
          (props.financials.fundingSummary?.spouseLivingCost.amount ?? 0) +
            (props.financials.fundingSummary?.childrenLivingCost.amount ?? 0),
        )}`}
      />
      <Finances
        showRisks={showRisks}
        organisationSponsorship={props.financials.organisationSponsorship ?? {}}
        scholarshipSponsorship={props.financials.scholarshipSponsorship ?? {}}
        educationSponsorship={props.financials.educationSponsorship ?? {}}
        familySponsorship={props.financials.familySponsorship ?? {}}
        otherSponsorship={props.financials.otherSponsorship ?? {}}
        risk={props.risks}
        courseFeeDepositReqd={props.financials.courseFeeDepositReqd ?? ''}
        courseFeeDepositDate={props.financials.courseFeeDepositDate ?? ''}
        courseFeeDeposit={`£${formatCurrency(props.financials.courseFeeDeposit ?? 0)}`}
        courseFeeDepositNotReqdReason={props.financials.courseFeeDepositNotReqdReason ?? ''}
        accomDepositReqd={props.financials.accomDepositReqd ?? ''}
        accomDepositDate={props.financials.accomDepositDate ?? ''}
        accomDeposit={`£${formatCurrency(props.financials.accomDeposit ?? 0)}`}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Previous Study
      </Heading>
      <SummaryRow
        className={'visa-summary'}
        key={Q.LAST_COURSE}
        label="What was the last course you successfully completed?"
        value={props.history.lastCourse}
      />
      {props.history.lastCourse === 'Other' && (
        <SummaryRow
          className={'visa-summary'}
          key={Q.LAST_COURSE_OTHER}
          label="Name"
          fullLabel="Please state why you chose other:"
          value={props.history.lastCourseOther}
        />
      )}
      <SummaryRow
        className={'visa-summary'}
        key={Q.LAST_COURSE_YEAR}
        label="Year"
        fullLabel="In which year did you complete this course?"
        value={props.history.lastCourseYear}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Course Details
      </Heading>
      <SummaryRow className={'visa-summary'} key="school-name" label="School Name" value={props.sponsor.schoolName} />
      {props.offer.courses?.map((course: ICourse, i: number) => (
        <React.Fragment key={`course-${i}`}>
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-name-${i}`}
            label={`Course Name ${i + 1}:`}
            value={course.courseName}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-start-date-${i}`}
            label={`Start ${i + 1}:`}
            value={course.startDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-end-date-${i}`}
            label={`End ${i + 1}:`}
            value={course.endDate}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-fee-${i}`}
            label={`Fee ${i + 1}:`}
            value={course.courseFee}
          />
          <SummaryRow
            className={'visa-summary'}
            key={`${Q.COURSES}-is-work-placement-${i}`}
            label={`Is Work Placement ${i + 1}:`}
            value={course.isWorkPlacement}
          />
        </React.Fragment>
      ))}
      <br />
      <SummaryRow
        className={'visa-summary'}
        key={VQ.ASSESSED_BY_SPONSOR}
        label={'Assessed by sponsor'}
        fullLabel={
          'Have you been assessed by the sponsor by other means, for example references or a portfolio of artwork?'
        }
        value={boolToYesNo(props.visa.assessedBySponsor)}
      />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Additional information
      </Heading>
      <Feedback {...props.visa} />
      <Heading as={'h3'} fontSize={'lg'} mt={6} mb={2}>
        Provided documents
      </Heading>
      <Box key={'academic-documents'}>
        <Heading as="h4" fontSize={'md'}>
          {'Academic/Travel Documents'}
        </Heading>
        {renderDocs(Object.values(props.documents.cas))}
        {renderDocs(Object.values(props.documents.passport))}
        {renderDocs(Object.values(props.documents.academicCertificate))}
        {renderDocs(Object.values(props.documents.academicTranscript))}
        {Object.values(props.documents.studentVisas).map((studentVisa: StudentVisaDocuments) => {
          return (
            <>
              {renderDocs(Object.values(studentVisa.visa ?? {}))}
              {renderDocs(Object.values(studentVisa.cas ?? {}))}
              {renderDocs(Object.values(studentVisa.biometric ?? {}))}
            </>
          );
        })}
        {Object.values(props.documents.visaRefusals).map((visaRefusal: UKVisaRefusal) =>
          renderDocs(Object.values(visaRefusal.visas)),
        )}
        {Object.values(props.documents.nonUkVisaRefusals).map((nonUKVisaRefusal: NonUKVisaRefusal) =>
          renderDocs(Object.values(nonUKVisaRefusal.visas)),
        )}
        {Object.values(props.documents.otherVisas).map((otherVisa: OtherVisaDocs) =>
          renderDocs(Object.values(otherVisa.visa ?? {})),
        )}
        {renderDocs(Object.values(props.documents.parentalConsent))}
        {renderDocs(Object.values(props.documents.tbTest))}
        {renderDocs(Object.values(props.documents.atas))}
        {renderDocs(Object.values(props.documents.englishTest))}
        {renderDocs(Object.values(props.documents.postStudyLetter))}
        {Object.values(props.documents.customRequest).map((customRequestDocument: CustomRequest) =>
          renderDocs(Object.values(customRequestDocument.documents ?? {})),
        )}
      </Box>
      <Box key={'finance-documents'}>
        <Heading as="h4" fontSize={'md'}>
          Financial Documents
        </Heading>
        {/* {renderDocs(Object.values(props.financeDocuments.deposit.documents))}
        {Object.values(props.financeDocuments.familySponsorship).map(
          familySponsor => {
            return (
              <>
                {[
                  renderDocs(Object.values(familySponsor.bankStatement)),
                  renderDocs(Object.values(familySponsor.bankTranslation)),
                  renderDocs(Object.values(familySponsor.bankLetter)),
                  renderDocs(Object.values(familySponsor.parentLetter))
                ]}
              </>
            );
          }
        )}
        {Object.values(props.financeDocuments.birthCertificate).map(bCert => {
          return (
            <>
              {[
                renderDocs(Object.values(bCert.certificate)),
                renderDocs(Object.values(bCert.translation))
              ]}
            </>
          );
        })}
        {Object.values(props.financeDocuments.governmentSponsorship).map(
          govDocs => {
            return <>{renderDocs(Object.values(govDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.companySponsorship).map(
          compDocs => {
            return <>{renderDocs(Object.values(compDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.scholarshipSponsorship).map(
          scholDocs => {
            return <>{renderDocs(Object.values(scholDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.educationSponsorship).map(
          eduDocs => {
            return <>{renderDocs(Object.values(eduDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.educationSponsorship).map(
          eduDocs => {
            return <>{renderDocs(Object.values(eduDocs.documents))}</>;
          }
        )}
        {Object.values(props.financeDocuments.customRequest).map(customDocs => {
          return <>{renderDocs(Object.values(customDocs.documents))}</>;
        })} */}
      </Box>
    </>
  );
}

export default VisaPrepPreview;
